import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Tabs,
  Tab,
  Grid,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Autocomplete } from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { invoiceAPI } from '../api/api';
import { vendorAPI } from '../api/api';
import { categoryAPI } from '../api/api';

const AliasManagement = () => {
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [selectedAliasVendor, setSelectedAliasVendor] = useState('');
  const [selectedAliasCategory, setSelectedAliasCategory] = useState('');
  const [selectedStandardVendor, setSelectedStandardVendor] = useState('');
  const [selectedStandardCategory, setSelectedStandardCategory] = useState('');
  const [activeTab, setActiveTab] = useState(0); // 0 for vendors, 1 for categories
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [vendorsResponse, categoriesResponse, invoicesResponse] = await Promise.all([
        vendorAPI.getVendors(),
        categoryAPI.getCategories(),
        invoiceAPI.getInvoices(0, 100) // Get invoices to get AI-extracted names
      ]);
      
      setVendors(vendorsResponse.data);
      setCategories(categoriesResponse.data);
      setInvoices(invoicesResponse.data);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVendorAlias = async (e) => {
    e.preventDefault();
    
    if (!selectedAliasVendor) {
      setError('Please select an alias name');
      return;
    }
    
    if (!selectedStandardVendor) {
      setError('Please select or enter a standard vendor name');
      return;
    }

    try {
      // Find the vendor that has the selected alias name as its name field
      let vendor = vendors.find(v => v.name.toLowerCase() === selectedAliasVendor.toLowerCase());
      
      if (!vendor) {
        // If the vendor doesn't exist yet, create it with the alias name
        const response = await vendorAPI.createVendor({ name: selectedAliasVendor });
        vendor = response.data;
        setSuccess('New vendor created and alias linked successfully');
        // Refresh vendors list
        const vendorsResponse = await vendorAPI.getVendors();
        setVendors(vendorsResponse.data);
      }

      // Update the vendor's standard_name field to the user-entered standard name
      await vendorAPI.updateVendor(vendor.id, { name: vendor.name, standard_name: selectedStandardVendor });
      setSelectedAliasVendor('');
      setSelectedStandardVendor('');
      setSuccess('Vendor alias updated successfully');
      // Refresh the list
      const vendorsResponse = await vendorAPI.getVendors();
      setVendors(vendorsResponse.data);
    } catch (error) {
      console.error('Error updating vendor alias:', error);
      setError('Failed to update vendor alias: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleCreateCategoryAlias = async (e) => {
    e.preventDefault();
    
    if (!selectedAliasCategory) {
      setError('Please select an alias name');
      return;
    }
    
    if (!selectedStandardCategory) {
      setError('Please select or enter a standard category name');
      return;
    }

    try {
      // Find the category that has the selected alias name as its name field
      let category = categories.find(c => c.name.toLowerCase() === selectedAliasCategory.toLowerCase());
      
      if (!category) {
        // If the category doesn't exist yet, create it with the alias name
        const response = await categoryAPI.createCategory({ name: selectedAliasCategory });
        category = response.data;
        setSuccess('New category created and alias linked successfully');
        // Refresh categories list
        const categoriesResponse = await categoryAPI.getCategories();
        setCategories(categoriesResponse.data);
      }

      // Update the category's standard_name field to the user-entered standard name
      await categoryAPI.updateCategory(category.id, { name: category.name, standard_name: selectedStandardCategory });
      setSelectedAliasCategory('');
      setSelectedStandardCategory('');
      setSuccess('Category alias updated successfully');
      // Refresh the list
      const categoriesResponse = await categoryAPI.getCategories();
      setCategories(categoriesResponse.data);
    } catch (error) {
      console.error('Error updating category alias:', error);
      setError('Failed to update category alias: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteAlias = async (vendorOrCategoryId, type) => {
    try {
      if (type === 'vendor') {
        // Remove the standard name by setting it to null
        await vendorAPI.updateVendor(vendorOrCategoryId, { standard_name: null });
        setSuccess('Vendor alias removed successfully');
        // Refresh the list
        const vendorsResponse = await vendorAPI.getVendors();
        setVendors(vendorsResponse.data);
      } else {
        // Remove the standard name by setting it to null
        await categoryAPI.updateCategory(vendorOrCategoryId, { standard_name: null });
        setSuccess('Category alias removed successfully');
        // Refresh the list
        const categoriesResponse = await categoryAPI.getCategories();
        setCategories(categoriesResponse.data);
      }
    } catch (error) {
      console.error('Error removing alias:', error);
      setError('Failed to remove alias: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Get unique alias names from invoices (AI responses) - these are the names that AI extracted
  const getUniqueAliasNames = (type) => {
    if (type === 'vendor') {
      // Get vendor names from invoices (AI-extracted names)
      const invoiceVendorNames = invoices.map(invoice => invoice.vendor?.name).filter(name => name);
      // Also include vendor names that have standard names set
      const vendorWithStandardNames = vendors.filter(vendor => vendor.standard_name).map(vendor => vendor.standard_name);
      // Return unique names
      return [...new Set([...invoiceVendorNames, ...vendorWithStandardNames])];
    } else {
      // Get category names from invoices (AI-extracted names)
      const invoiceCategoryNames = invoices.map(invoice => invoice.category?.name).filter(name => name);
      // Also include category names that have standard names set
      const categoryWithStandardNames = categories.filter(category => category.standard_name).map(category => category.standard_name);
      // Return unique names
      return [...new Set([...invoiceCategoryNames, ...categoryWithStandardNames])];
    }
  };

  // Get vendors/categories that have standard names set
  const getVendorsWithStandardNames = () => {
    return vendors.filter(vendor => vendor.standard_name && vendor.standard_name.trim() !== '');
  };

  const getCategoriesWithStandardNames = () => {
    return categories.filter(category => category.standard_name && category.standard_name.trim() !== '');
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box mb={4}>
        <Typography variant="h4" component="h1" gutterBottom>
          Alias Management
        </Typography>
        <Typography variant="body1" color="textSecondary" paragraph>
          Manage vendor and category aliases for your organization. Set standard names to standardize AI-extracted names across your invoices.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Vendor Aliases" />
          <Tab label="Category Aliases" />
        </Tabs>
      </Paper>

      {/* Vendor Aliases Tab */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Add New Vendor Alias
                </Typography>
                <Box component="form" onSubmit={handleCreateVendorAlias} sx={{ mt: 2 }}>
                  {/* AI Response (Alias Name) - dropdown only */}
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="alias-vendor-select-label">AI Response (Alias Name)</InputLabel>
                    <Select
                      labelId="alias-vendor-select-label"
                      value={selectedAliasVendor}
                      label="AI Response (Alias Name)"
                      onChange={(e) => setSelectedAliasVendor(e.target.value)}
                      required
                    >
                      <MenuItem value="">
                        <em>Select AI-extracted name</em>
                      </MenuItem>
                      {getUniqueAliasNames('vendor').map((aliasName, index) => (
                        <MenuItem key={index} value={aliasName}>
                          {aliasName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  {/* Standard Name - with autocomplete */}
                  <Autocomplete
                    freeSolo
                    fullWidth
                    margin="normal"
                    options={vendors.filter(v => v.standard_name && v.standard_name.trim() !== '').map(v => v.standard_name)} // Use vendor standard names as options
                    value={selectedStandardVendor}
                    onChange={(event, newValue) => {
                      if (newValue && typeof newValue === 'string') {
                        setSelectedStandardVendor(newValue);
                      }
                    }}
                    onInputChange={(event, newInputValue) => {
                      if (newInputValue !== undefined) {
                        setSelectedStandardVendor(newInputValue);
                      }
                    }}
                    getOptionLabel={(option) => option}
                    renderOption={(props, option) => (
                      <li {...props}>
                        {option}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Standard Vendor Name (Type or Select)" 
                        placeholder="Enter or select standard vendor name"
                      />
                    )}
                  />
                  
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<AddIcon />}
                    fullWidth
                    sx={{ mt: 2 }}
                  >
                    Set Vendor Alias
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Existing Vendor Aliases
                </Typography>
                {getVendorsWithStandardNames().length > 0 ? (
                  <List>
                    {getVendorsWithStandardNames().map((vendor) => (
                      <ListItem key={vendor.id} divider>
                        <ListItemText
                          primary={vendor.standard_name}
                          secondary={`Maps to: ${vendor.name}`}
                        />
                        <ListItemSecondaryAction>
                          <IconButton 
                            edge="end" 
                            aria-label="delete" 
                            color="error"
                            onClick={() => handleDeleteAlias(vendor.id, 'vendor')}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 3 }}>
                    No vendor aliases found.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Category Aliases Tab */}
      {activeTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Add New Category Alias
                </Typography>
                <Box component="form" onSubmit={handleCreateCategoryAlias} sx={{ mt: 2 }}>
                  {/* Alias Name (AI response) - dropdown only */}
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="alias-category-select-label">Select Alias Name (AI Response)</InputLabel>
                    <Select
                      labelId="alias-category-select-label"
                      value={selectedAliasCategory}
                      label="Select Alias Name (AI Response)"
                      onChange={(e) => setSelectedAliasCategory(e.target.value)}
                      required
                    >
                      <MenuItem value="">
                        <em>Choose an alias name from AI response</em>
                      </MenuItem>
                      {getUniqueAliasNames('category').map((aliasName, index) => (
                        <MenuItem key={index} value={aliasName}>
                          {aliasName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  {/* Standard Name - with autocomplete */}
                  <Autocomplete
                    freeSolo
                    fullWidth
                    margin="normal"
                    options={categories.filter(c => c.standard_name && c.standard_name.trim() !== '').map(c => c.standard_name)} // Use category standard names as options
                    value={selectedStandardCategory}
                    onChange={(event, newValue) => {
                      if (newValue && typeof newValue === 'string') {
                        setSelectedStandardCategory(newValue);
                      }
                    }}
                    onInputChange={(event, newInputValue) => {
                      if (newInputValue !== undefined) {
                        setSelectedStandardCategory(newInputValue);
                      }
                    }}
                    getOptionLabel={(option) => option}
                    renderOption={(props, option) => (
                      <li {...props}>
                        {option}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Standard Category Name (Type or Select)" 
                        placeholder="Enter or select standard category name"
                      />
                    )}
                  />
                  
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<AddIcon />}
                    fullWidth
                    sx={{ mt: 2 }}
                  >
                    Set Category Alias
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Existing Category Aliases
                </Typography>
                {getCategoriesWithStandardNames().length > 0 ? (
                  <List>
                    {getCategoriesWithStandardNames().map((category) => (
                      <ListItem key={category.id} divider>
                        <ListItemText
                          primary={category.standard_name}
                          secondary={`Maps to: ${category.name}`}
                        />
                        <ListItemSecondaryAction>
                          <IconButton 
                            edge="end" 
                            aria-label="delete" 
                            color="error"
                            onClick={() => handleDeleteAlias(category.id, 'category')}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 3 }}>
                    No category aliases found.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default AliasManagement;